<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6 flex-wrap">
      <div>
        <h1 class="text-h4 font-weight-light mb-1">Comprobantes de Caja</h1>
        <div class="text-caption grey--text">
          Gestión de ingresos y egresos • {{ formatDateHeader(issueDate) }}
        </div>
      </div>

      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primary" v-bind="attrs" v-on="on" class="mt-2 mt-sm-0">
            <v-icon left>mdi-plus</v-icon>
            Nuevo
          </v-btn>
        </template>
        <v-list>
          <v-list-item to="Receipt_in">
            <v-list-item-icon>
              <v-icon color="success">mdi-cash-plus</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Comprobante de Ingreso</v-list-item-title>
              <v-list-item-subtitle>Registrar entrada de dinero</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item to="Receipt_out">
            <v-list-item-icon>
              <v-icon color="error">mdi-cash-minus</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Comprobante de Egreso</v-list-item-title>
              <v-list-item-subtitle>Registrar salida de dinero</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- KPI Cards Mejorados -->
    <v-row class="mb-6">
      <v-col cols="6" md="6">
        <v-card flat outlined height="100%">
          <v-card-text class="text-center py-4">
            <v-avatar size="48" color="primary" class="mb-3">
              <v-icon color="white">mdi-file-document-multiple</v-icon>
            </v-avatar>
            <div class="text-h5 font-weight-bold">{{ credit_notes.length }}</div>
            <div class="text-caption grey--text">Total Documentos</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" md="6">
        <v-card flat outlined height="100%">
          <v-card-text class="text-center py-4">
            <v-avatar
              size="48"
              :color="getTotalValue() >= 0 ? 'success' : 'error'"
              class="mb-3"
            >
              <v-icon color="white">
                {{ getTotalValue() >= 0 ? "mdi-trending-up" : "mdi-trending-down" }}
              </v-icon>
            </v-avatar>
            <div
              class="text-h6 font-weight-bold"
              :class="getTotalValue() >= 0 ? 'success--text' : 'error--text'"
            >
              {{ formatCurrency(getTotalValue()) }}
            </div>
            <div class="text-caption grey--text">Balance del Día</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Card -->
    <v-card flat outlined class="mb-6">
      <v-card-text>
        <v-row align="center" no-gutters>
          <v-col cols="12" md="3" class="pr-md-2">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="issueDate"
                  label="Fecha"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="issueDate"
                @input="
                  dateMenu = false;
                  get_notes();
                "
                locale="es-ES"
                first-day-of-week="1"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="3" class="px-md-2 mt-3 mt-md-0">
            <v-select
              v-model="selectedDocType"
              :items="docTypes"
              label="Tipo"
              prepend-inner-icon="mdi-filter"
              outlined
              dense
              hide-details
              clearable
              @change="filterDocuments"
            />
          </v-col>

          <v-col cols="12" md="3" class="px-md-2 mt-3 mt-md-0">
            <v-select
              v-model="selectedCategory"
              :items="categoryOptions"
              label="Categoría"
              prepend-inner-icon="mdi-tag"
              outlined
              dense
              hide-details
              clearable
              @change="filterDocuments"
            />
          </v-col>

          <v-col cols="12" md="3" class="pl-md-2 mt-3 mt-md-0">
            <v-text-field
              v-model="search"
              label="Buscar..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-card flat outlined>
      <v-toolbar flat>
        <v-icon class="mr-2">mdi-table</v-icon>
        <v-toolbar-title class="text-h6">Movimientos del Día</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          text
          color="success"
          @click="exportToExcel"
          :disabled="credit_notes.length === 0"
        >
          <v-icon left>mdi-microsoft-excel</v-icon>
          Exportar
        </v-btn>
        <v-btn
          text
          color="primary"
          @click="openPrintDrawer"
          :disabled="credit_notes.length === 0"
        >
          <v-icon left>mdi-printer</v-icon>
          Imprimir
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <!-- Desktop View -->
      <v-data-table
        v-if="$vuetify.breakpoint.smAndUp"
        :headers="headers"
        :items="filteredDocuments"
        :search="search"
        :items-per-page="15"
        sort-by="issuedate"
        sort-desc
        :loading="loading_status"
        loading-text="Cargando documentos..."
        item-key="doc_number"
        class="clickable-table"
      >
        <template v-slot:[`item.doc_type`]="{ item }">
          <v-chip small :color="getDocumentTypeColor(item.doc_type)">
            <v-icon left x-small>
              {{ getDocumentTypeIcon(item.doc_type) }}
            </v-icon>
            {{ getDocumentTypeName(item.doc_type) }}
          </v-chip>
        </template>

        <template v-slot:[`item.category`]="{ item }">
          <div v-if="item.items && item.items.length > 0">
            <span class="text-body-2">
              {{ item.items[0].item_concept || "Sin categoría" }}
            </span>
          </div>
          <div v-else class="text-caption grey--text">Sin categoría</div>
        </template>

        <template v-slot:[`item.concept`]="{ item }">
          <div v-if="item.items && item.items.length > 0">
            <div class="text-body-2">
              {{ item.items[0].item_note || "Sin nota" }}
            </div>
          </div>
          <div v-else class="text-caption grey--text">Sin nota</div>
        </template>

        <template v-slot:[`item.expense_type`]="{ item }">
          <span v-if="item.doc_type === 'CS-OUT'" class="text-caption">
            {{ getExpenseType(item) }}
          </span>
          <span v-else class="text-caption grey--text">-</span>
        </template>

        <template v-slot:[`item.issuedate`]="{ item }">
          <div>
            <div class="font-weight-medium">{{ formatDate(item.issuedate) }}</div>
            <div class="text-caption grey--text">{{ formatTime(item.issuedate) }}</div>
          </div>
        </template>

        <template v-slot:[`item.invoice`]="{ item }">
          <div class="d-flex align-center">
            <v-icon small class="mr-2" :color="getDocumentTypeColor(item.doc_type)">
              mdi-file-document
            </v-icon>
            <span class="font-weight-medium">{{ item.invoice || item.doc_number }}</span>
          </div>
        </template>

        <template v-slot:[`item.PartyName`]="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.PartyName || "Sin tercero" }}</div>
            <div class="text-caption grey--text" v-if="item.PartyIdentification">
              {{ item.PartyIdentification }}
            </div>
          </div>
        </template>

        <template v-slot:[`item.payment_method`]="{ item }">
          <div v-if="item.payments && item.payments.length > 0">
            <span class="text-body-2">
              {{ item.payments[0].payment_concept }}
            </span>
          </div>
          <div v-else class="text-caption grey--text">Sin método</div>
        </template>

        <template v-slot:[`item.doc_value`]="{ item }">
          <div class="text-right">
            <span
              class="text-h6 font-weight-bold"
              :class="isIngresoDocument(item.doc_type) ? 'success--text' : 'error--text'"
            >
              {{ isIngresoDocument(item.doc_type) ? "+" : "-"
              }}{{ formatCurrency(item.doc_value) }}
            </span>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn icon small @click.stop="get_e_doc(item)">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </template>

        <!-- Empty state -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-file-document-outline</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay documentos</h3>
            <p class="grey--text mt-2">
              {{
                search
                  ? "No se encontraron documentos con ese criterio"
                  : "No hay documentos en la fecha seleccionada"
              }}
            </p>
          </div>
        </template>
      </v-data-table>

      <!-- Mobile View sigue igual... -->
    </v-card>

    <!-- Resumen de Caja -->
    <v-card flat outlined class="mt-6">
      <v-card-title>Resumen de Caja</v-card-title>
      <v-simple-table>
        <thead>
          <tr>
            <th>Concepto</th>
            <th class="text-right">Ingresos</th>
            <th class="text-right">Egresos</th>
            <th class="text-right">Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="method in getPaymentMethodsSummary()" :key="method.label">
            <td>{{ method.label }}</td>
            <td class="text-right">
              {{ method.value > 0 ? formatCurrency(method.value) : "-" }}
            </td>
            <td class="text-right">
              {{ method.value >= 0 ? "-" : formatCurrency(Math.abs(method.value)) }}
            </td>
            <td class="text-right">{{ formatCurrency(method.value) }}</td>
          </tr>
          <tr class="font-weight-bold">
            <td>Total</td>
            <td class="text-right">{{ formatCurrency(getTotalIngresos()) }}</td>
            <td class="text-right">{{ formatCurrency(getTotalEgresos()) }}</td>
            <td class="text-right">{{ formatCurrency(getTotalValue()) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- Resumen por Forma de Pago -->
    <v-card flat outlined class="mt-6">
      <v-card-title>Resumen por Forma de Pago</v-card-title>
      <v-simple-table>
        <thead>
          <tr>
            <th>Forma de Pago</th>
            <th class="text-right">Ingresos</th>
            <th class="text-right">Egresos</th>
            <th class="text-right">Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="payment in getPaymentMethodsByTypeSummary()" :key="payment.label">
            <td>{{ payment.label }}</td>
            <td class="text-right">
              {{ payment.ingresos > 0 ? formatCurrency(payment.ingresos) : "-" }}
            </td>
            <td class="text-right">
              {{ payment.egresos > 0 ? formatCurrency(payment.egresos) : "-" }}
            </td>
            <td class="text-right">
              {{ formatCurrency(payment.ingresos - payment.egresos) }}
            </td>
          </tr>
          <tr class="font-weight-bold">
            <td>Total</td>
            <td class="text-right">{{ formatCurrency(getTotalIngresos()) }}</td>
            <td class="text-right">{{ formatCurrency(getTotalEgresos()) }}</td>
            <td class="text-right">{{ formatCurrency(getTotalValue()) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- Document Viewer Dialog -->
    <noteViewer
      :item="item"
      :dialog="dialog"
      @close="dialog = false"
      @refresh="refresh"
    />

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>

    <!-- Cash Report Printer -->
    <CashReportPrinter
      :drawer.sync="printDrawer"
      :report-data="printReportData"
      @printed="onReportPrinted"
      @print-error="onPrintError"
    />
  </v-container>
</template>

<script>
import { webserver, getToday, formatCurrency } from "@/services/webserver.js";
import noteViewer from "@/components/doc_viewer.vue";
import CashReportPrinter from "@/components/CashReportPrinter.vue";

export default {
  name: "CashDocs",
  components: { noteViewer, CashReportPrinter },

  data() {
    return {
      // Loading state
      loading_status: false,

      // UI state
      dialog: false,
      dateMenu: false,
      currentPage: 1,

      // Filters
      search: "",
      selectedDocType: "ALL",
      selectedCategory: null,
      issueDate: getToday(),

      // Data
      credit_notes: [],
      item: { doc: null, items: [], payments: [] },

      // Configuración dinámica para conceptos de items
      availableColors: [
        "primary",
        "success",
        "error",
        "warning",
        "info",
        "orange",
        "purple",
        "teal",
        "pink",
        "indigo",
        "cyan",
        "deep-orange",
        "brown",
        "blue-grey",
      ],
      dynamicConceptConfig: {}, // Se llena automáticamente con los conceptos encontrados

      // Clasificación de documentos mejorada
      documentClassification: {
        // INGRESOS A CAJA (todos estos generan dinero)
        ingresos: ["TK", "FS", "FE", "FC", "CS-IN"],
        // EGRESOS DE CAJA (todos estos sacan dinero)
        egresos: ["CS-OUT", "OP", "GASTOS", "SERVICIOS", "NOMINA", "NC", "ND"],
      },

      // Options mejoradas
      docTypes: [
        { text: "Todos", value: "ALL" },
        { text: "Ingresos", value: "INGRESO" },
        { text: "Egresos", value: "EGRESO" },
        { text: "Facturas", value: "FACTURAS" },
        { text: "Comprobantes", value: "COMPROBANTES" },
      ],

      categoryOptions: [
        { text: "Ventas", value: "VENTAS" },
        { text: "Compras", value: "COMPRAS" },
        { text: "Gastos", value: "GASTOS" },
        { text: "Devoluciones", value: "DEVOLUCIONES" },
        { text: "Notas", value: "NOTAS" },
      ],

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Print Drawer
      printDrawer: false,

      // Table headers actualizados
      headers: [
        {
          text: "Fecha",
          value: "issuedate",
          width: "160px",
        },
        {
          text: "Tipo",
          value: "doc_type",
          align: "left",
          width: "120px",
        },
        {
          text: "Categoría",
          value: "category",
          align: "left",
          width: "150px",
        },
        {
          text: "Concepto",
          value: "concept",
          align: "left",
          width: "200px",
        },
        {
          text: "Tipo de Egreso",
          value: "expense_type",
          align: "left",
          width: "180px",
        },
        {
          text: "Documento",
          value: "invoice",
          width: "180px",
        },
        {
          text: "Tercero",
          value: "PartyName",
        },
        {
          text: "Método Pago",
          value: "payment_method",
          align: "left",
          width: "120px",
        },
        {
          text: "Valor",
          value: "doc_value",
          align: "right",
          width: "140px",
        },
        {
          text: "",
          value: "actions",
          align: "center",
          sortable: false,
          width: "80px",
        },
      ],
    };
  },

  computed: {
    filteredDocuments() {
      let filtered = this.credit_notes;

      // Filtrar por tipo principal
      if (this.selectedDocType !== "ALL") {
        switch (this.selectedDocType) {
          case "INGRESO":
            filtered = filtered.filter((doc) => this.isIngresoDocument(doc.doc_type));
            break;
          case "EGRESO":
            filtered = filtered.filter((doc) => !this.isIngresoDocument(doc.doc_type));
            break;
          case "FACTURAS":
            filtered = filtered.filter((doc) =>
              ["TK", "FS", "FE", "FC"].includes(doc.doc_type)
            );
            break;
          case "COMPROBANTES":
            filtered = filtered.filter((doc) =>
              ["CS-IN", "CS-OUT"].includes(doc.doc_type)
            );
            break;
        }
      }

      // Filtrar por categoría
      if (this.selectedCategory) {
        filtered = filtered.filter(
          (doc) => this.getDocumentCategory(doc.doc_type) === this.selectedCategory
        );
      }

      return filtered;
    },

    paginatedDocuments() {
      const startIndex = (this.currentPage - 1) * 10;
      const endIndex = startIndex + 10;
      return this.filteredDocuments.slice(startIndex, endIndex);
    },

    printReportData() {
      const paymentMethods = this.getPaymentMethodsSummary().map((method) => ({
        ...method,
        value: Math.abs(method.value),
      }));
      const totalSales = Math.abs(this.getTotalValue());
      return {
        date: this.issueDate,
        totalSales: totalSales,
        totalTransactions: this.credit_notes.length,
        averageTicket:
          this.credit_notes.length > 0 ? totalSales / this.credit_notes.length : 0,
        paymentMethods: paymentMethods,
        paymentMethodsByType: this.getPaymentMethodsByTypeSummary(),
        documentTypes: this.getDocumentTypesSummary(),
        vendors: [], // No tenemos info de vendedores en este componente
        documents: this.credit_notes,
      };
    },
  },

  mounted() {
    this.get_notes();
  },

  methods: {
    // Métodos de clasificación de documentos
    isIngresoDocument(docType) {
      return this.documentClassification.ingresos.includes(docType);
    },

    getDocumentCategory(docType) {
      const categoryMap = {
        TK: "VENTAS",
        FS: "VENTAS",
        FE: "VENTAS",
        FC: "VENTAS",
        "CS-IN": "VENTAS",
        "CS-OUT": "GASTOS", // Changed from DEVOLUCIONES to GASTOS
        NC: "NOTAS",
        ND: "NOTAS",
        GASTOS: "GASTOS",
      };
      return categoryMap[docType] || "OTROS";
    },

    getDocumentTypeName(docType) {
      const nameMap = {
        TK: "TICKET",
        FS: "FACTURA SIMPLE",
        FE: "FACTURA ELECTRÓNICA",
        FC: "FACTURA DE CONTINGENCIA",
        "CS-IN": "INGRESO",
        "CS-OUT": "EGRESO",
        NC: "NOTA CRÉDITO",
        ND: "NOTA DÉBITO",
        GASTOS: "GASTO",
      };
      return nameMap[docType] || docType;
    },

    getDocumentTypeColor(docType) {
      if (this.isIngresoDocument(docType)) {
        return "success";
      } else {
        return "error";
      }
    },

    getDocumentTypeIcon(docType) {
      if (this.isIngresoDocument(docType)) {
        return "mdi-arrow-down";
      } else {
        return "mdi-arrow-up";
      }
    },

    getCategoryColor(docType) {
      const category = this.getDocumentCategory(docType);
      const colorMap = {
        VENTAS: "success",
        COMPRAS: "warning",
        GASTOS: "error",
        DEVOLUCIONES: "orange",
        NOTAS: "info",
      };
      return colorMap[category] || "grey";
    },

    getPaymentMethodColor(paymentConcept) {
      if (!paymentConcept) return "grey";
      const lowerConcept = paymentConcept.toLowerCase();

      if (lowerConcept.includes("efectivo")) return "success";
      if (lowerConcept.includes("transferencia") || lowerConcept.includes("banco"))
        return "primary";
      if (lowerConcept.includes("tarjeta") || lowerConcept.includes("credito"))
        return "info";
      if (lowerConcept.includes("cheque")) return "warning";

      return "grey";
    },

    getPaymentMethodIcon(paymentConcept) {
      if (!paymentConcept) return "mdi-cash";
      const lowerConcept = paymentConcept.toLowerCase();

      if (lowerConcept.includes("efectivo")) return "mdi-cash";
      if (lowerConcept.includes("transferencia") || lowerConcept.includes("banco"))
        return "mdi-bank";
      if (lowerConcept.includes("tarjeta") || lowerConcept.includes("credito"))
        return "mdi-credit-card";
      if (lowerConcept.includes("cheque")) return "mdi-check";

      return "mdi-cash";
    },

    // Sistema dinámico de conceptos
    generateDynamicConceptConfig() {
      const concepts = new Set();

      // Recopilar todos los conceptos únicos de los documentos
      this.credit_notes.forEach((doc) => {
        if (doc.items && doc.items.length > 0) {
          doc.items.forEach((item) => {
            if (item.item_concept) {
              concepts.add(item.item_concept);
            }
          });
        }
      });

      // Asignar colores automáticamente a cada concepto
      const conceptsArray = Array.from(concepts);
      this.dynamicConceptConfig = {};

      conceptsArray.forEach((concept, index) => {
        const colorIndex = index % this.availableColors.length;
        this.dynamicConceptConfig[concept] = {
          color: this.availableColors[colorIndex],
          icon: this.getIconForConcept(concept),
          category: this.categorizeConcept(concept),
        };
      });
    },

    getIconForConcept(concept) {
      const lowerConcept = concept.toLowerCase();

      if (lowerConcept.includes("gasto") || lowerConcept.includes("pago")) {
        return "mdi-cash-minus";
      } else if (lowerConcept.includes("compra") || lowerConcept.includes("mercaderia")) {
        return "mdi-cart";
      } else if (lowerConcept.includes("personal") || lowerConcept.includes("nomina")) {
        return "mdi-account-group";
      } else if (
        lowerConcept.includes("ingreso") ||
        lowerConcept.includes("venta") ||
        lowerConcept.includes("financiero")
      ) {
        return "mdi-cash-plus";
      } else {
        return "mdi-circle";
      }
    },

    categorizeConcept(concept) {
      const lowerConcept = concept.toLowerCase();

      if (lowerConcept.includes("gasto")) {
        return "GASTOS";
      } else if (lowerConcept.includes("compra") || lowerConcept.includes("mercaderia")) {
        return "COMPRAS";
      } else if (lowerConcept.includes("personal") || lowerConcept.includes("nomina")) {
        return "NOMINA";
      } else if (
        lowerConcept.includes("ingreso") ||
        lowerConcept.includes("venta") ||
        lowerConcept.includes("financiero")
      ) {
        return "INGRESOS";
      } else {
        return "OTROS";
      }
    },

    getConceptConfig(concept) {
      return (
        this.dynamicConceptConfig[concept] || {
          color: "grey",
          icon: "mdi-circle",
          category: "SIN CLASIFICAR",
        }
      );
    },

    getDocumentItemsList(doc) {
      if (!doc.items || doc.items.length === 0) return [];

      return doc.items.map((item, index) => {
        const quantity = parseFloat(item.item_quantity) || 1;
        const price = parseFloat(item.item_price) || 0;
        const total = price * quantity;

        return {
          id: `${doc.doc_number}_${index}`, // ID único basado en documento + índice
          concept: item.item_concept || "Sin concepto",
          note: item.item_note || "",
          quantity: quantity,
          price: price,
          total: total,
          config: this.getConceptConfig(item.item_concept),
        };
      });
    },

    getExpenseType(doc) {
      if (doc.doc_type !== "CS-OUT") return "";
      if (!doc.items || doc.items.length === 0) return "";

      const items = doc.items;

      // Si hay múltiples items con conceptos diferentes, mostrar resumen
      if (items.length > 1) {
        const concepts = [...new Set(items.map((item) => item.item_concept))];
        if (concepts.length > 1) {
          return `${concepts.length} conceptos`;
        }
      }

      // Mostrar información del primer item
      const item = items[0];
      const concept = item.item_concept || "";
      const note = item.item_note || "";

      // Categorizar basado en el concepto
      if (concept.toLowerCase().includes("gasto")) {
        return note ? `Gastos: ${note}` : "Gastos";
      } else if (
        concept.toLowerCase().includes("compra") ||
        concept.toLowerCase().includes("mercaderia")
      ) {
        return note ? `Compras: ${note}` : "Compras";
      } else if (
        concept.toLowerCase().includes("personal") ||
        concept.toLowerCase().includes("nomina")
      ) {
        return note ? `Nómina: ${note}` : "Nómina";
      } else {
        return concept || "Otro";
      }
    },

    getDocumentsByType(type) {
      if (type === "INGRESO") {
        return this.credit_notes.filter((doc) => this.isIngresoDocument(doc.doc_type));
      } else if (type === "EGRESO") {
        return this.credit_notes.filter((doc) => !this.isIngresoDocument(doc.doc_type));
      }
      return [];
    },

    // Date formatting
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDateMobile(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
      });
    },

    formatDateHeader(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    // Currency formatting
    formatCurrency,

    // Calculations actualizados
    getTotalValue() {
      let total = 0;
      this.credit_notes.forEach((doc) => {
        if (this.isIngresoDocument(doc.doc_type)) {
          total += parseFloat(doc.doc_value || 0);
        } else {
          total -= parseFloat(doc.doc_value || 0);
        }
      });
      return total;
    },

    getTotalIngresos() {
      return this.credit_notes
        .filter((doc) => this.isIngresoDocument(doc.doc_type))
        .reduce((sum, doc) => sum + parseFloat(doc.doc_value || 0), 0);
    },

    getTotalEgresos() {
      return this.credit_notes
        .filter((doc) => !this.isIngresoDocument(doc.doc_type))
        .reduce((sum, doc) => sum + parseFloat(doc.doc_value || 0), 0);
    },

    // Métodos para resumen de formas de pago
    getCashIngresos() {
      return this.credit_notes
        .filter(
          (doc) =>
            this.isIngresoDocument(doc.doc_type) &&
            doc.payments &&
            doc.payments.some(
              (p) =>
                p.payment_concept && p.payment_concept.toLowerCase().includes("efectivo")
            )
        )
        .reduce((sum, doc) => {
          const cashPayments = doc.payments.filter(
            (p) =>
              p.payment_concept && p.payment_concept.toLowerCase().includes("efectivo")
          );
          const totalAmount = cashPayments.reduce(
            (s, p) => s + parseFloat(p.amount || 0),
            0
          );
          return sum + (totalAmount || parseFloat(doc.doc_value || 0));
        }, 0);
    },

    getCashEgresos() {
      return this.credit_notes
        .filter(
          (doc) =>
            !this.isIngresoDocument(doc.doc_type) &&
            doc.payments &&
            doc.payments.some(
              (p) =>
                p.payment_concept && p.payment_concept.toLowerCase().includes("efectivo")
            )
        )
        .reduce((sum, doc) => {
          const cashPayments = doc.payments.filter(
            (p) =>
              p.payment_concept && p.payment_concept.toLowerCase().includes("efectivo")
          );
          const totalAmount = cashPayments.reduce(
            (s, p) => s + parseFloat(p.amount || 0),
            0
          );
          return sum + (totalAmount || parseFloat(doc.doc_value || 0));
        }, 0);
    },

    getCardIngresos() {
      return this.credit_notes
        .filter(
          (doc) =>
            this.isIngresoDocument(doc.doc_type) &&
            doc.payments &&
            doc.payments.some(
              (p) =>
                p.payment_concept &&
                (p.payment_concept.toLowerCase().includes("tarjeta") ||
                  p.payment_concept.toLowerCase().includes("credito"))
            )
        )
        .reduce((sum, doc) => {
          const cardPayments = doc.payments.filter(
            (p) =>
              p.payment_concept &&
              (p.payment_concept.toLowerCase().includes("tarjeta") ||
                p.payment_concept.toLowerCase().includes("credito"))
          );
          const totalAmount = cardPayments.reduce(
            (s, p) => s + parseFloat(p.amount || 0),
            0
          );
          return sum + (totalAmount || parseFloat(doc.doc_value || 0));
        }, 0);
    },

    getOtherIngresos() {
      return this.credit_notes
        .filter(
          (doc) =>
            this.isIngresoDocument(doc.doc_type) &&
            doc.payments &&
            doc.payments.some(
              (p) =>
                p.payment_concept &&
                !p.payment_concept.toLowerCase().includes("efectivo") &&
                !p.payment_concept.toLowerCase().includes("tarjeta") &&
                !p.payment_concept.toLowerCase().includes("credito")
            )
        )
        .reduce((sum, doc) => {
          const otherPayments = doc.payments.filter(
            (p) =>
              p.payment_concept &&
              !p.payment_concept.toLowerCase().includes("efectivo") &&
              !p.payment_concept.toLowerCase().includes("tarjeta") &&
              !p.payment_concept.toLowerCase().includes("credito")
          );
          const totalAmount = otherPayments.reduce(
            (s, p) => s + parseFloat(p.amount || 0),
            0
          );
          return sum + (totalAmount || parseFloat(doc.doc_value || 0));
        }, 0);
    },

    getOtherEgresos() {
      return this.credit_notes
        .filter(
          (doc) =>
            !this.isIngresoDocument(doc.doc_type) &&
            doc.payments &&
            doc.payments.some(
              (p) =>
                p.payment_concept && !p.payment_concept.toLowerCase().includes("efectivo")
            )
        )
        .reduce((sum, doc) => {
          const otherPayments = doc.payments.filter(
            (p) =>
              p.payment_concept && !p.payment_concept.toLowerCase().includes("efectivo")
          );
          const totalAmount = otherPayments.reduce(
            (s, p) => s + parseFloat(p.amount || 0),
            0
          );
          return sum + (totalAmount || parseFloat(doc.doc_value || 0));
        }, 0);
    },

    getPaymentMethodsSummary() {
      const paymentMap = {};
      this.credit_notes.forEach((doc) => {
        let concept = "";
        let value = 0;

        // Para FE y FC: agrupar por tipo de documento
        if (doc.doc_type === "FE" || doc.doc_type === "FC") {
          concept = this.getDocumentTypeName(doc.doc_type);
          value = parseFloat(doc.doc_value || 0);
        }
        // Para CS-IN y CS-OUT: detalle por conceptos de items
        else if (doc.doc_type === "CS-IN" || doc.doc_type === "CS-OUT") {
          if (doc.items && doc.items.length > 0) {
            doc.items.forEach((item) => {
              const itemConcept = item.item_concept || "Sin definir";
              if (!paymentMap[itemConcept]) {
                paymentMap[itemConcept] = { label: itemConcept, qty: 0, value: 0 };
              }
              paymentMap[itemConcept].qty += 1;
              const itemTotal =
                (parseFloat(item.item_quantity) || 1) *
                (parseFloat(item.item_price) || 0);
              paymentMap[itemConcept].value += itemTotal;
            });
            return; // Salir del forEach principal para CS-IN/CS-OUT con items
          } else {
            concept = "Sin concepto";
            const sign = this.isIngresoDocument(doc.doc_type) ? 1 : -1;
            value = parseFloat(doc.doc_value || 0) * sign;
          }
        }
        // Para otros tipos de documento
        else {
          concept = this.getDocumentTypeName(doc.doc_type);
          const sign = this.isIngresoDocument(doc.doc_type) ? 1 : -1;
          value = parseFloat(doc.doc_value || 0) * sign;
        }

        // Agregar al mapa si no es CS-IN/CS-OUT con items (que ya se procesaron arriba)
        if (concept) {
          if (!paymentMap[concept]) {
            paymentMap[concept] = { label: concept, qty: 0, value: 0 };
          }
          paymentMap[concept].qty += 1;
          paymentMap[concept].value += value;
        }
      });
      return Object.values(paymentMap).sort(
        (a, b) => Math.abs(b.value) - Math.abs(a.value)
      );
    },

    getDocumentTypesSummary() {
      const types = {};
      this.credit_notes.forEach((doc) => {
        const type = this.getDocumentTypeName(doc.doc_type);
        if (!types[type]) {
          types[type] = { label: type, qty: 0, value: 0 };
        }
        types[type].qty += 1;
        types[type].value +=
          parseFloat(doc.doc_value || 0) *
          (this.isIngresoDocument(doc.doc_type) ? 1 : -1);
      });
      return Object.values(types);
    },

    getPaymentMethodsByTypeSummary() {
      const paymentMap = {};
      this.credit_notes.forEach((doc) => {
        if (doc.payments && doc.payments.length > 0) {
          doc.payments.forEach((payment) => {
            const concept = payment.payment_concept || "Sin definir";
            if (!paymentMap[concept]) {
              paymentMap[concept] = { label: concept, ingresos: 0, egresos: 0 };
            }
            const amount = parseFloat(payment.amount || doc.doc_value || 0);
            if (this.isIngresoDocument(doc.doc_type)) {
              paymentMap[concept].ingresos += amount;
            } else {
              paymentMap[concept].egresos += amount;
            }
          });
        }
      });
      return Object.values(paymentMap).sort(
        (a, b) => b.ingresos + b.egresos - (a.ingresos + a.egresos)
      );
    },

    // Message handling
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Data methods
    refresh() {
      this.get_notes();
    },

    filterDocuments() {
      this.currentPage = 1;
    },

    get_notes() {
      this.loading_status = true;

      const qry = {
        store: window.store.store_id,
        doc_types: ["CS-IN", "CS-OUT", "FE", "FC", "FS", "TK", "NC", "ND"],
        start_date: this.issueDate,
        end_date: this.issueDate,
        userId: this.$store.state.profile.email,
      };
      console.log("Consultando documentos con:", qry);

      webserver(
        "get_docs_by_types",
        qry,
        (data) => {
          console.log("Documentos recibidos:", data);
          this.loading_status = false;
          // TRANSFORMACIÓN: aplanar doc, items y payments
          this.credit_notes = (data || []).map((d) => ({
            ...d.doc,
            items: d.items,
            payments: d.payments,
          }));
          this.currentPage = 1;

          // Generar configuración dinámica de conceptos
          this.generateDynamicConceptConfig();

          this.showMessage(`${data.length} documentos cargados`, "success");
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar documentos", "error");
          console.error(error);
        }
      );
    },

    get_e_doc(item) {
      const issudate = item.issuedate.split(" ");
      const doc = {
        store: item.store,
        issueDate: issudate[0],
        did: item.doc_number,
      };

      this.loading_status = true;
      webserver(
        "get_invoice_fe",
        doc,
        (data) => {
          this.loading_status = false;
          this.item = data;
          this.dialog = true;
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar el documento", "error");
          console.error(error);
        }
      );
    },

    exportToExcel() {
      this.showMessage("Función de exportación en desarrollo", "info");
      // TODO: Implement Excel export functionality
    },

    openPrintDrawer() {
      this.printDrawer = true;
    },

    onReportPrinted() {
      this.showMessage("Reporte impreso correctamente", "success");
      this.printDrawer = false;
    },

    onPrintError(error) {
      this.showMessage("Error al imprimir: " + error.message, "error");
    },
  },
};
</script>

<style scoped>
.clickable-table >>> tbody tr {
  cursor: pointer;
}

.clickable-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
</style>
